import Navbar from "@/components/Layout/Navbar";
import Footer from "@/components/Layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, ExternalLink, MapPin, Users, Code, Award, Globe, TrendingUp } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { PAGE_SEO_CONFIG } from "@/lib/seo";
import { useTranslation } from "react-i18next";
import TrustBar from "@/components/EEAT/TrustBar";
import ArticleMeta from "@/components/EEAT/ArticleMeta";
import AuthorBio from "@/components/EEAT/AuthorBio";

const Companies = () => {
  const { t, i18n } = useTranslation(['companies', 'common']);
  const seoConfig = PAGE_SEO_CONFIG.companies;
  const currentLang = i18n.language;

  const getArrayTranslation = (key: string): string[] => {
    const result = t(key, { returnObjects: true });
    if (Array.isArray(result)) {
      return result.filter((item): item is string => typeof item === 'string');
    }
    return [];
  };

  const companies = [
    {
      name: "Evolvice",
      location: currentLang === 'de' ? "Ägypten" : "Egypt",
      foundedYear: "2012",
      employees: "200+",
      description: t('companies:evolvice.description'),
      longDescription: t('companies:evolvice.longDescription'),
      specializations: getArrayTranslation('companies:evolvice.specializations'),
      highlights: getArrayTranslation('companies:evolvice.highlights'),
      technologies: getArrayTranslation('companies:evolvice.technologies'),
      link: currentLang === 'de' ? "https://www.evolvice.de" : "https://www.evolvice.de/en",
      featured: true,
    },
    {
      name: "Moringa School",
      location: currentLang === 'de' ? "Kenia" : "Kenya",
      foundedYear: "2014",
      employees: "50+",
      description: t('companies:moringa.description'),
      longDescription: "",
      specializations: [],
      highlights: getArrayTranslation('companies:moringa.highlights'),
      technologies: [],
      link: "https://moringaschool.com/",
      featured: false,
    },
    {
      name: "CodeLn",
      location: "Ghana",
      foundedYear: "2019",
      employees: "30+",
      description: t('companies:codeln.description'),
      longDescription: "",
      specializations: [],
      highlights: getArrayTranslation('companies:codeln.highlights'),
      technologies: [],
      link: "https://www.codeln.com/",
      featured: false,
    },
  ];

  const stats = [
    { icon: Award, value: "500+", label: t('companies:stats.projects') },
    { icon: Users, value: "200+", label: t('companies:stats.developers') },
    { icon: Globe, value: "30+", label: t('companies:stats.countries') },
    { icon: TrendingUp, value: "98%", label: t('companies:stats.satisfaction') },
  ];

  const evolviceUrl = currentLang === 'de' ? 'https://www.evolvice.de' : 'https://www.evolvice.de/en';

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        titleEn={seoConfig.titleEn}
        titleDe={seoConfig.titleDe}
        descriptionEn={seoConfig.descriptionEn}
        descriptionDe={seoConfig.descriptionDe}
        keywords={seoConfig.keywords}
        path={seoConfig.path}
        breadcrumbs={[
          { name: 'Home', nameDE: 'Startseite', url: '/' },
          { name: 'Companies', nameDE: 'Unternehmen', url: '/companies' }
        ]}
      />
      <Navbar />
      <main className="pt-24">
        {/* Hero */}
        <section className="py-32">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
                {t('companies:hero.subtitle')}
              </div>
              <h1 className="text-5xl md:text-6xl font-semibold tracking-[-0.035em] text-balance mb-6">
                {t('companies:hero.title')}
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                {t('companies:hero.description')}
              </p>
              <div className="mt-8 flex justify-center">
                <ArticleMeta publishedDate="2025-08-15" updatedDate="2026-04-19" />
              </div>
            </div>
          </div>
        </section>

        {/* Trust */}
        <TrustBar />

        {/* Stats */}
        <section className="pb-24">
          <div className="container mx-auto px-6">
            <Card className="max-w-5xl mx-auto">
              <CardContent className="p-10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  {stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                      <div key={index} className="text-center">
                        <Icon className="w-5 h-5 text-muted-foreground mx-auto mb-3" aria-hidden="true" />
                        <div className="text-3xl font-semibold tracking-[-0.02em] mb-1">
                          {stat.value}
                        </div>
                        <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                          {stat.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Companies */}
        <section className="pb-24">
          <div className="container mx-auto px-6">
            <div className="space-y-6 max-w-6xl mx-auto">
              {companies.map((company, index) => (
                <Card key={index} className="group">
                  <CardContent className="p-10 md:p-12">
                    {company.featured && (
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border/60 bg-background/60 text-xs uppercase tracking-[0.15em] text-muted-foreground mb-5">
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground" />
                        {t('companies:featuredPartner')}
                      </div>
                    )}

                    <h3 className="text-3xl md:text-4xl font-semibold tracking-[-0.028em] mb-4">
                      {company.name}
                    </h3>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground mb-6">
                      <span className="flex items-center gap-1.5">
                        <MapPin size={14} className="opacity-60" />
                        {company.location}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Award size={14} className="opacity-60" />
                        {t('companies:companyInfo.founded')} {company.foundedYear}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Users size={14} className="opacity-60" />
                        {company.employees} {t('companies:companyInfo.employees')}
                      </span>
                    </div>

                    <p className="text-lg text-foreground/80 mb-4 leading-relaxed">
                      {company.description}
                    </p>

                    {company.longDescription && (
                      <p className="text-muted-foreground mb-6 leading-relaxed">
                        {company.longDescription}
                      </p>
                    )}

                    {company.specializations.length > 0 && (
                      <div className="mb-8 pt-6 border-t border-border/60">
                        <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4 flex items-center gap-2">
                          <Code size={14} />
                          {t('companies:sections.specializations')}
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {company.specializations.map((spec, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="w-4 h-4 text-foreground/40 flex-shrink-0" />
                              <span className="text-foreground/70">{spec}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {company.highlights.length > 0 && (
                      <div className="mb-8 pt-6 border-t border-border/60">
                        <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
                          {t('companies:sections.highlights')}
                        </h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {company.highlights.map((highlight, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                              <CheckCircle2 className="w-4 h-4 text-foreground/40 flex-shrink-0 mt-1" />
                              <span className="text-foreground/70 text-sm">{highlight}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {company.technologies.length > 0 && (
                      <div className="mb-8 pt-6 border-t border-border/60">
                        <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
                          {t('companies:sections.technologies')}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {company.technologies.map((tech, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 rounded-full border border-border/60 bg-background/60 text-xs text-foreground/70"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button variant="glass" asChild>
                      <a href={company.link} target="_blank" rel="noopener noreferrer" aria-label={`Visit ${company.name} website (opens in new tab)`}>
                        {t('companies:buttons.learnMore')}
                        <ExternalLink size={14} aria-hidden="true" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Evolvice Spotlight */}
        <section className="py-24">
          <div className="container mx-auto px-6">
            <Card className="max-w-4xl mx-auto">
              <CardContent className="p-10 md:p-14">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">
                  Spotlight
                </div>
                <h3 className="text-3xl md:text-4xl font-semibold tracking-[-0.028em] mb-6">
                  {t('companies:whyEvolvice.title')}
                </h3>
                <p className="text-lg text-foreground/80 leading-relaxed mb-5">
                  {t('companies:whyEvolvice.paragraph1')}
                </p>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  {t('companies:whyEvolvice.paragraph2')}
                </p>
                <Button asChild>
                  <a href={evolviceUrl} target="_blank" rel="noopener noreferrer" aria-label="Visit Evolvice GmbH website (opens in new tab)">
                    {t('companies:whyEvolvice.button')}
                    <ExternalLink size={14} aria-hidden="true" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Author */}
        <section className="py-16 border-t border-border/60">
          <div className="container mx-auto px-6 max-w-3xl">
            <AuthorBio
              authorId="nearshore-research"
              reviewedByDe="Sherif Adel, MD Evolvice GmbH"
              reviewedByEn="Sherif Adel, MD Evolvice GmbH"
            />
          </div>
        </section>

        {/* CTA */}
        <section className="py-32">
          <div className="container mx-auto px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-semibold tracking-[-0.028em] text-balance mb-6">
                {t('companies:cta.title')}
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {t('companies:cta.subtitle')}
              </p>
              <Button size="lg" asChild>
                <a href={evolviceUrl} target="_blank" rel="noopener noreferrer" aria-label="Start your nearshore partnership with Evolvice (opens in new tab)">
                  {t('companies:cta.button')}
                  <ExternalLink size={16} aria-hidden="true" />
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Companies;
